import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Save, X, AlertTriangle, Camera } from "lucide-react";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { db, storage, getFeaturedProducts } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { validateImageFile } from "../utils/imageValidation";
import Card from "../Card/Card";
import { ProductType } from "../../types/product";
import Cropper from "react-easy-crop";
import { cropImageFile } from "../utils/cropImage";
import { resizeImage } from "../utils/resizeImage";

// Types
interface ProductProps {
  onSaveSuccess?: () => void;
}

const Product: React.FC<ProductProps> = ({ onSaveSuccess }) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);

  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<any>(null);
  const [crop, setCrop] = useState({ x: 16, y: 9 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [selectedAspectRatioType, setSelectedAspectRatioType] =
    useState("square");

  useEffect(() => {
    // Fetching products and storing in desc order to show latest created at top of grid
    (async () => {
      try {
        setLoading(true);
        const shopList = await getFeaturedProducts();
        setProducts(shopList);
      } catch (err) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const validateProduct = useCallback(() => {
    if (!title.trim()) {
      setError("Title is required");
      return false;
    }
    if (!url.trim()) {
      setError("URL is required");
      return false;
    }
    const urlRegex = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    if (!urlRegex.test(url.trim())) {
      setError("Invalid URL format");
      return false;
    }
    if (!selectedImage) {
      setError("Image is required");
      return false;
    }
    setError(null);
    return true;
  }, [title, url, selectedImage]);

  // const handleImageChange = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const file = e.target.files?.[0];
  //     if (!file) return;

  //     const validationError = validateImageFile(file);
  //     if (validationError) {
  //       setError(validationError);
  //       return;
  //     }

  //     if (file) {
  //       setSelectedImage(file);
  //       setImagePreview(URL.createObjectURL(file));
  //       setError(null);
  //     }
  //   },
  //   []
  // );

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (fileInputRef.current) fileInputRef.current.value = "";

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(validationError);
        setError(validationError);
        return;
      }

      setLoadingCropper(true);
      try {
        const { resizedUrl: resized } = await resizeImage(file);
        setTempImageUrl(resized);
        setShowCropper(true);
        setError(null);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const resetImageState = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleDelete = useCallback(async (productId: string) => {
    try {
      await deleteDoc(doc(db, "product", productId));
      setProducts((prev) => prev.filter((product) => product.id !== productId));
    } catch (error) {
      console.error("Failed to delete product:", error);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!validateProduct()) return;

    setIsSaving(true);
    try {
      let imageUrl = null;
      if (selectedImage) {
        const storageRef = ref(
          storage,
          `product/${Date.now()}_${selectedImage.name}`
        );
        await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(storageRef);
      }

      const productData = {
        title: title.trim(),
        image: imageUrl,
        url: url.trim(),
      };

      const docRef = await addDoc(collection(db, "product"), {
        ...productData,
        createdAt: serverTimestamp(),
      });

      setProducts((prev) => [{ id: docRef.id, ...productData }, ...prev]);
      onSaveSuccess?.();
      setTitle("");
      setSelectedImage(null);
      setImagePreview(null);
      setUrl("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setError(null);
    } catch (error) {
      console.error("Error saving product:", error);
      setError("Failed to save product. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [selectedImage, title, url, validateProduct, onSaveSuccess]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
    },
    []
  );

  const imageSection = useMemo(
    () => (
      <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
        <div className="space-y-2 text-center">
          <div className="flex flex-col items-center">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 rounded-lg"
                />
                <button
                  type="button"
                  onClick={resetImageState}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <Camera size={48} className="mx-auto text-gray-400" />
            )}
          </div>
          <label
            htmlFor="image-blog-upload"
            className="cursor-pointer text-[#18f7b1] hover:text-[#0fd494]"
          >
            {imagePreview ? "Change image" : "Upload an image"}
            <input
              id="image-blog-upload"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="sr-only"
            />
          </label>
          <p className="text-sm text-gray-500">
            PNG, JPG, JPEG, WEBP up to 10MB
          </p>
        </div>
      </div>
    ),
    [imagePreview, handleImageChange, resetImageState]
  );

  const handleAspectRatioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAspectRatioType(e.target.value);
    const aspectType = e.target.value;
    if (aspectType === "portrait") {
      setCrop({ x: 9, y: 16 });
    } else if (aspectType === "landscape") {
      setCrop({ x: 16, y: 9 });
    }
    resetImageState();
  };

  const getAspectRatio = () => {
    if (selectedAspectRatioType === "portrait") {
      return 9 / 16;
    } else if (selectedAspectRatioType === "landscape") {
      return 16 / 9;
    } else if (selectedAspectRatioType === "square") {
      return 1;
    }
  };

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;

    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setSelectedImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }

    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  return (
    <>
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-2xl font-bold mb-4">Product Information</h2>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <AlertTriangle className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={handleTitleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                placeholder="Enter product title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Product URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                placeholder="https://your-product.com"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium mb-2">
                Product Image
              </label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
                <div className="space-y-2 text-center">
                  <div className="flex flex-col items-center">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-48 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <Camera size={48} className="mx-auto text-gray-400" />
                    )}
                  </div>

                  <label
                    htmlFor="image-upload"
                    className="relative cursor-pointer rounded-md font-medium text-[#18f7b1] hover:text-[#0fd494]"
                  >
                    {imagePreview ? "Change image" : "Upload an image"}
                    <input
                      id="image-upload"
                      ref={fileInputRef}
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>

                  <p className="text-sm text-gray-500">
                    PNG, JPG, JPEG, WEBP up to 10MB
                  </p>
                </div>
              </div>
            </div> */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Product Image
              </label>
              <select
                value={selectedAspectRatioType}
                onChange={handleAspectRatioChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                defaultValue={selectedAspectRatioType}
              >
                <option value="landscape">Landscape (16:9)</option>
                <option value="portrait">Portrait (9:16)</option>
                <option value="square">Square (1:1)</option>
              </select>
              {imageSection}
            </div>
            {loadingCropper && (
              <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
                <div className="text-white text-xl flex items-center gap-4">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-white border-opacity-70" />
                  Preparing image for cropping...
                </div>
              </div>
            )}
            {showCropper && tempImageUrl && (
              <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                  <Cropper
                    image={tempImageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={getAspectRatio()}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                  <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                    <button
                      onClick={() => setShowCropper(false)}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCropConfirm}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Crop & Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] flex items-center gap-2 transition-all duration-200 ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-8 max-w-4xl mx-auto">
        <h3 className="text-xl font-semibold mb-4 text-center">
          Products to be seen in Homepage
        </h3>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin h-8 w-8 border-4 border-[#18f7b1] border-t-transparent rounded-full"></div>
          </div>
        ) : products.length === 0 ? (
          <p className="text-center text-gray-500">No products saved yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto max-h-[450px]">
            {products.map((product) => (
              <Card
                key={product.id}
                title={product.title}
                subtitle={
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {product.url}
                  </a>
                }
                imageUrl={product.image}
                deleteModalProps={{
                  onConfirm: () => handleDelete(product.id),
                  description: "Are you sure you want to delete this product?",
                  isDeleting: loading,
                  // buttonText: "Delete",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(Product);
